import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Search, X, Package, ArrowRight, Loader2 } from "lucide-react";
import api from "../lib/api";

/**
 * NavbarSearch - Autocomplete search component for the navbar.
 * Shows live product results as user types.
 */
export default function NavbarSearch({ onSearch, isCompact = false }) {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const debounceRef = useRef(null);
    const wrapperRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const searchProducts = (q) => {
        if (q.length < 2) { setResults([]); setOpen(false); return; }
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/products?search=${encodeURIComponent(q)}&limit=6`);
                const items = data?.items || data || [];
                setResults(items.slice(0, 6));
                setOpen(true);
            } catch {
                setResults([]);
            }
            setLoading(false);
        }, 250);
    };

    const handleChange = (e) => {
        const v = e.target.value;
        setQuery(v);
        searchProducts(v);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setOpen(false);
        onSearch?.(query);
    };

    const handleSelect = () => {
        setOpen(false);
        setQuery("");
        setResults([]);
    };

    const h = isCompact ? "34px" : "38px";

    return (
        <div className="relative flex-1 max-w-xl" ref={wrapperRef} data-testid="navbar-search-wrapper">
            <form onSubmit={handleSubmit}>
                <div className="flex border-2 rounded-lg overflow-hidden" style={{ borderColor: "var(--sg-primary)", height: h }}>
                    <input
                        type="text"
                        value={query}
                        onChange={handleChange}
                        onFocus={() => results.length > 0 && setOpen(true)}
                        placeholder="Hledat produkty, značky..."
                        className="flex-1 px-3 text-gray-700 outline-none bg-white"
                        style={{ fontSize: "13px" }}
                        data-testid="navbar-search-input"
                    />
                    {loading && <Loader2 size={14} className="animate-spin text-gray-400 self-center mr-2" />}
                    {query && !loading && (
                        <button type="button" onClick={() => { setQuery(""); setResults([]); setOpen(false); }}
                            className="self-center mr-1 text-gray-400 hover:text-gray-600 p-1">
                            <X size={12} />
                        </button>
                    )}
                    <button type="submit"
                        className="px-4 text-white bg-green-600 hover:bg-green-700 transition-colors flex items-center"
                        data-testid="navbar-search-btn">
                        <Search size={16} />
                    </button>
                </div>
            </form>

            {/* Autocomplete dropdown */}
            {open && results.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-[500] overflow-hidden"
                    data-testid="search-autocomplete-dropdown">
                    {results.map(p => (
                        <Link key={p.id} to={`/produkt/${p.slug}`}
                            className="flex items-center gap-3 px-3 py-2.5 hover:bg-green-50 transition-colors border-b border-gray-50 last:border-0 group"
                            onClick={handleSelect}
                            data-testid={`search-result-${p.slug}`}>
                            <div className="w-10 h-10 rounded-lg bg-gray-50 border border-gray-100 flex-shrink-0 overflow-hidden flex items-center justify-center">
                                {p.main_image ? (
                                    <img src={p.main_image} alt="" className="w-full h-full object-contain p-0.5" loading="lazy" />
                                ) : (
                                    <Package size={14} className="text-gray-300" />
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                {p.brand && <p className="text-[9px] text-gray-400 font-medium uppercase">{p.brand}</p>}
                                <p className="text-xs font-medium text-gray-700 group-hover:text-green-700 leading-tight truncate transition-colors">{p.name}</p>
                            </div>
                            <div className="text-right flex-shrink-0">
                                <p className="text-sm font-bold" style={{ color: "var(--sg-primary)" }}>
                                    {(p.sale_price || p.price || 0).toLocaleString("cs-CZ")} Kč
                                </p>
                                {p.sale_price && p.price > p.sale_price && (
                                    <p className="text-[9px] text-gray-400 line-through">{p.price.toLocaleString("cs-CZ")} Kč</p>
                                )}
                            </div>
                        </Link>
                    ))}
                    {/* See all results */}
                    <button onClick={handleSubmit}
                        className="w-full flex items-center justify-center gap-1.5 py-2.5 bg-gray-50 hover:bg-green-50 text-sm font-medium text-green-700 transition-colors"
                        data-testid="search-see-all">
                        Zobrazit všechny výsledky <ArrowRight size={14} />
                    </button>
                </div>
            )}

            {/* No results */}
            {open && query.length >= 2 && results.length === 0 && !loading && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-[500] p-6 text-center"
                    data-testid="search-no-results">
                    <p className="text-sm text-gray-400">Žádné výsledky pro "{query}"</p>
                </div>
            )}
        </div>
    );
}
